import { StatFlags } from './StatTypes'; // eslint-disable-next-line @typescript-eslint/no-var-requires

var StatsApi = require('./stats-api');

var NEXT_STAT_HANDLER_ID = 1; // NOTE: special ID that will not be associated with the user,
// this is for cases when we want fully anonymous data

var FULLY_ANONYMOUS_ID;

function getFullyAnonymousId() {
  if (!FULLY_ANONYMOUS_ID) {
    FULLY_ANONYMOUS_ID = createAnonymousId();
  }

  return FULLY_ANONYMOUS_ID;
}

function createAnonymousId() {
  return StatsApi.UUID();
}

export function identify(statHandler, apiKey, user) {
  if (!user.id) {
    // NOTE: this is to avoid accidental calls for guests who has no id
    statHandler.app.sendError('Stats invalid identify', 'Empty user id');
    return;
  }

  var anonymousId = statHandler.anonymousId;
  var userId = statHandler.userId;

  if (userId !== user.id) {
    // NOTE: we reset anonymousId only if user was previously set, otherwise just update the userId
    if (userId) {
      anonymousId = createAnonymousId();
    }

    userId = user.id;
    statHandler.app.storeUserIds({
      userId: userId,
      anonymousId: anonymousId
    });
    updateUserForHandler(statHandler, anonymousId, userId, 'identify');
  }

  var params = {
    apiKey: apiKey,
    apiHost: statHandler.apiHost,
    context: getStatContext(statHandler),
    timestamp: new Date(),
    anonymousId: anonymousId,
    userId: userId,
    user: {
      id: user.id,
      name: user.name,
      email: user.email
    }
  };
  sendEvent(statHandler, StatFlags.None, {
    event: 'identify',
    payload: params
  });
}

function buildTrackParams(statHandler, apiKey, event, inputProperties, flags, timestamp) {
  var result = undefined;
  var onlyFirstSession = flags & StatFlags.OnlyFirstSession;

  if (!onlyFirstSession || statHandler.isFirstSession) {
    var canBeSend = true;
    var onceInSession = flags & StatFlags.OnceInSession;

    if (onceInSession) {
      canBeSend = !statHandler.onceInSessionSentEvents.has(event);

      if (canBeSend) {
        statHandler.onceInSessionSentEvents.add(event);
      }
    }

    if (canBeSend) {
      var properties = inputProperties || {};

      if (!(flags & StatFlags.NoCommonParams)) {
        properties = statHandler.app.addCommonStatParams(properties);
      }

      var asAnonymous = !!(flags & StatFlags.Anonymous);
      var userIds = getUserIdsPayload(statHandler, asAnonymous);
      result = {
        apiKey: apiKey,
        apiHost: statHandler.apiHost,
        context: getStatContext(statHandler),
        timestamp: timestamp,
        event: event,
        properties: properties,
        anonymousId: userIds.anonymousId,
        userId: userIds.userId
      };
    }
  }

  return result;
}

function buildBatchFlags(flags) {
  var batchFlags = StatFlags.None;

  if (flags & StatFlags.OnlyForWorker) {
    batchFlags |= StatFlags.OnlyForWorker;
  }

  return batchFlags;
}

export function page(statHandler, apiKey, properties) {
  var flags = arguments.length > 3 && arguments[3] !== undefined ? arguments[3] : StatFlags.None;
  var asAnonymous = !!(flags & StatFlags.Anonymous);
  var userIds = getUserIdsPayload(statHandler, asAnonymous);
  var context = getStatContext(statHandler); // Title and path can be overriden from properties

  var _ref = properties || {},
      _ref$path = _ref.path,
      path = _ref$path === void 0 ? '' : _ref$path,
      _ref$title = _ref.title,
      title = _ref$title === void 0 ? '' : _ref$title;

  if (path) {
    context.page.path = path;
  }

  if (title) {
    context.page.title = title;
  }

  var params = {
    apiKey: apiKey,
    apiHost: statHandler.apiHost,
    context: getStatContext(statHandler),
    timestamp: new Date(),
    anonymousId: userIds.anonymousId,
    userId: userIds.userId
  };
  sendEvent(statHandler, StatFlags.None, {
    event: 'page',
    payload: params
  });
}
export function track(statHandler, apiKey, event, inputProperties) {
  var flags = arguments.length > 4 && arguments[4] !== undefined ? arguments[4] : StatFlags.None;
  var timestamp = arguments.length > 5 && arguments[5] !== undefined ? arguments[5] : new Date();
  var trackParams = buildTrackParams(statHandler, apiKey, event, inputProperties, flags, timestamp);

  if (trackParams) {
    if (!statHandler.batches || flags & StatFlags.SendImmediately) {
      sendEvent(statHandler, flags, {
        event: 'track',
        payload: trackParams
      });
    } else {
      var apiKeyBucket = statHandler.batches.buckets.get(apiKey);

      if (!apiKeyBucket) {
        apiKeyBucket = new Map();
        statHandler.batches.buckets.set(apiKey, apiKeyBucket);
      }

      var batchFlags = buildBatchFlags(flags);
      var batch = apiKeyBucket.get(batchFlags);

      if (!batch) {
        batch = {
          tracks: [],
          flags: batchFlags,
          flushAt: statHandler.batches.flushAt
        };
        apiKeyBucket.set(batchFlags, batch);
      }

      if (batch.tracks.length >= batch.flushAt) {
        flushBatchInternal(statHandler, batch, apiKey);
      }

      batch.tracks.push(trackParams);
    }
  }
}
export function trackBatch(statHandler, apiKey, events) {
  var flags = arguments.length > 3 && arguments[3] !== undefined ? arguments[3] : StatFlags.None;
  var tracks = [];
  events.forEach(function (event) {
    var params = buildTrackParams(statHandler, apiKey, event.event, event.properties, flags, event.timestamp || new Date());

    if (params) {
      tracks.push(params);
    }
  });
  var tempBatch = {
    tracks: tracks,
    flags: buildBatchFlags(flags),
    flushAt: tracks.length
  };
  flushBatchInternal(statHandler, tempBatch, apiKey);
}
export function clearBatches(statHandler) {
  if (statHandler.batches) {
    statHandler.batches.buckets.clear();
  }
}
export function flushBatches(statHandler) {
  if (statHandler.batches) {
    statHandler.batches.buckets.forEach(function (bucket, apiKey) {
      bucket.forEach(function (batch) {
        flushBatchInternal(statHandler, batch, apiKey);
      });
    });
  }
}
export function flushBatch(statHandler, apiKey) {
  if (statHandler.batches) {
    var bucket = statHandler.batches.buckets.get(apiKey);

    if (bucket) {
      bucket.forEach(function (batch) {
        flushBatchInternal(statHandler, batch, apiKey);
      });
    }
  }
}

function flushBatchInternal(statHandler, batch, apiKey) {
  if (batch.tracks.length) {
    var params = {
      apiKey: apiKey,
      apiHost: statHandler.apiHost,
      tracks: batch.tracks
    };
    sendEvent(statHandler, batch.flags, {
      event: 'trackBatch',
      payload: params
    });
    batch.tracks.length = 0;
  }
}

function getUserIdsPayload(statHandler, asAnonymous) {
  var result = {
    anonymousId: asAnonymous ? getFullyAnonymousId() : statHandler.anonymousId,
    userId: asAnonymous ? undefined : statHandler.userId
  };
  return result;
}

function sendEvent(statHandler, flags, params) {
  if (statHandler.worker) {
    statHandler.worker.postMessage(params);
  } else {
    var onlyWorker = !!(flags & StatFlags.OnlyForWorker);

    if (onlyWorker) {// noop, event should be sent only via worker
    } else {
      switch (params.event) {
        case 'identify':
          {
            StatsApi.identify(params.payload);
          }
          break;

        case 'track':
          {
            StatsApi.track(params.payload);
          }
          break;

        case 'trackBatch':
          {
            StatsApi.trackBatch(params.payload);
          }
          break;

        case 'page':
          {
            StatsApi.page(params.payload);
          }
          break;
      }
    }
  }
}

function getStatContext(statHandler) {
  var context = {
    library: {
      name: statHandler.library,
      version: statHandler.version
    },
    page: statHandler.app.getStatContextPage(statHandler),
    userAgent: statHandler.app.getUserAgent()
  };
  return context;
}

export function updateUserForHandler(statHandler, anonymousId, userId, source) {
  if (!anonymousId || userId === 'null' || userId === 'undefined') {
    statHandler.app.sendError('Stats invalid userId', {
      anonymousId: anonymousId,
      userId: userId,
      source: source
    });
  } else {
    statHandler.anonymousId = anonymousId;
    statHandler.userId = userId;
    statHandler.__lastUserSource = source;
  }
}
export function createStatHandler(options) {
  var statHandler = {
    __statId: NEXT_STAT_HANDLER_ID++,
    __lastUserSource: undefined,
    apiHost: options.apiHost,
    library: options.library,
    version: options.version,
    anonymousId: '',
    userId: undefined,
    app: options.app,
    // NOTE: just defaults, override them from outside
    isFirstSession: false,
    onceInSessionSentEvents: new Set(),
    // NOTE: no worker by default, set this from outside if you need to
    worker: undefined,
    batches: undefined,
    flushBatchesIntervalId: undefined
  };

  if (options.batches) {
    statHandler.batches = {
      buckets: new Map(),
      flushAt: options.batches.flushAt
    };
  }

  var userData = statHandler.app.getStoredUserIds();

  if (!userData.anonymousId) {
    userData.anonymousId = createAnonymousId();
    statHandler.app.storeUserIds(userData);
  }

  updateUserForHandler(statHandler, userData.anonymousId, userData.userId, 'initialization');
  return statHandler;
}
export function destroyStatHandler(statHandler) {// noop for now?
}